// Caltrans D4 CCTV cameras inside the SF box, with fog-suitability tier
// derived from a live visual eyeball (May 2026):
//
//   top:     wide depth, hill or skyline target, currently functioning
//   second:  useful but limited depth or partial obstruction
//   low:     short focal depth, lens grime, or boxed-in framing
//   offline: returned "Temporarily Unavailable" placeholder when polled

export type CameraTier = "top" | "second" | "low" | "offline";

export type CaltransCamera = {
  id: string;
  lat: number;
  lon: number;
  name: string;
  tier: CameraTier;
  note?: string;
  // Vibe-data fields: anchor for the synthesized 24h score trace. baseline =
  // eyeballed score from today's live frame (16:30 PDT 2026-05-27).
  // amplitude = how much the camera's view would swing across a typical
  // marine-layer day, based on what the lens can resolve (hilltop windward
  // cams: high; downtown skyline: low). Offline/low-value cams = 0.
  vibeBaseline?: number;
  vibeAmplitude?: number;
};

export const CALTRANS_CAMERAS: CaltransCamera[] = [
  // ── top tier: build the ingest around these six ───────────────────────────
  { id: "TV323", lat: 37.710995, lon: -122.455064, name: "I-280 at Sickles Av",       tier: "top",    vibeBaseline: 0.35, vibeAmplitude: 0.95, note: "windward Twin Peaks/Mt Davidson ridge" },
  { id: "TV317", lat: 37.77145,  lon: -122.3974,   name: "I-280 at 6th St offramp",   tier: "top",    vibeBaseline: 0.10, vibeAmplitude: 0.55, note: "elevated Bayview/Bernal skyline view" },
  { id: "TV304", lat: 37.77025,  lon: -122.40554,  name: "I-80 at JCT 101",           tier: "top",    vibeBaseline: 0.10, vibeAmplitude: 0.55, note: "downtown skyline target" },
  { id: "TV305", lat: 37.71069,  lon: -122.39526,  name: "US-101 at Candlestick",     tier: "top",    vibeBaseline: 0.20, vibeAmplitude: 0.85, note: "San Bruno foothills, south" },
  { id: "TV306", lat: 37.732,    lon: -122.4285,   name: "I-280 at Mission St",       tier: "top",    vibeBaseline: 0.25, vibeAmplitude: 0.90, note: "Mount Davidson approach" },
  { id: "TV379", lat: 37.71802,  lon: -122.3996,   name: "US-101 at 3rd Bayshore",    tier: "top",    vibeBaseline: 0.20, vibeAmplitude: 0.85, note: "Bayview Hill" },

  // ── second tier ──────────────────────────────────────────────────────────
  { id: "TV316", lat: 37.77696,  lon: -122.40347,  name: "I-80 at 6th St",            tier: "second", vibeBaseline: 0.10, vibeAmplitude: 0.50 },
  { id: "TVD01", lat: 37.78537,  lon: -122.39244,  name: "I-80 at Fremont (east)",    tier: "second", vibeBaseline: 0.25, vibeAmplitude: 0.70, note: "Bay Bridge depth target" },
  { id: "TVD31", lat: 37.812965, lon: -122.362228, name: "I-80 JEO Treasure Island",  tier: "second", vibeBaseline: 0.15, vibeAmplitude: 0.65, note: "Yerba Buena hillside" },

  // ── low value (still rendered but vibe muted) ────────────────────────────
  { id: "TV303", lat: 37.77001,  lon: -122.41983,  name: "S-101 at S Van Ness",       tier: "low",    vibeBaseline: 0,    vibeAmplitude: 0, note: "lens covered in water spots" },
  { id: "TV308", lat: 37.73839,  lon: -122.40828,  name: "S-101 at Alemany Bl",       tier: "low",    vibeBaseline: 0,    vibeAmplitude: 0, note: "no horizon visible" },
  { id: "TV301", lat: 37.77146,  lon: -122.42323,  name: "S-101 at Octavia St",       tier: "low",    vibeBaseline: 0,    vibeAmplitude: 0, note: "short focal depth" },
  { id: "TV325", lat: 37.74208,  lon: -122.39759,  name: "I-280 JSO Cesar Chavez",    tier: "low",    vibeBaseline: 0,    vibeAmplitude: 0, note: "concrete only" },

  // ── offline at last poll, worth re-trying every 30min ────────────────────
  { id: "TV388", lat: 37.79138,  lon: -122.46968,  name: "SR-1 South of Presidio Tunnel", tier: "offline", note: "highest-value offline cam (Presidio fog)" },
  { id: "TV327", lat: 37.71046,  lon: -122.4654,   name: "I-280 at John Daly Blvd",   tier: "offline", note: "Daly City fog gateway" },
  { id: "TV322", lat: 37.7343,   lon: -122.40983,  name: "I-280 at US-101",           tier: "offline" },
  { id: "TVD22", lat: 37.78432,  lon: -122.39341,  name: "I-80 SFOBB Lower Deck",     tier: "offline" },
  { id: "TV302", lat: 37.764283, lon: -122.405225, name: "US-101 at 17th St",         tier: "offline" },
  { id: "TV320", lat: 37.731347, lon: -122.435877, name: "I-280 on Monterey Bl",      tier: "offline" },
  { id: "TV314", lat: 37.756079, lon: -122.39198,  name: "I-280 at Indiana St",       tier: "offline" },
  { id: "TV311", lat: 37.75936,  lon: -122.4068,   name: "US-101 at Hospital Curve",  tier: "offline" },
  { id: "TV380", lat: 37.72781,  lon: -122.4029,   name: "US-101 at 280 Split",       tier: "offline" },
  { id: "TV326", lat: 37.7453,   lon: -122.39498,  name: "I-280 south of Cesar Chavez", tier: "offline" },
  { id: "TV321", lat: 37.732,    lon: -122.41496,  name: "I-280 at Alemany Bl",       tier: "offline" },
  { id: "TV313", lat: 37.7546,   lon: -122.4026,   name: "US-101 at 23rd St",         tier: "offline" },
  { id: "TV312", lat: 37.74823,  lon: -122.40434,  name: "US-101 at Cesar Chavez",    tier: "offline" },
  { id: "TV310", lat: 37.74379,  lon: -122.40555,  name: "US-101 at Faith St POC",    tier: "offline" },
  { id: "TV355", lat: 37.70349,  lon: -122.39344,  name: "US-101 at Candlestick OFR", tier: "offline" }
];

// IDW power=2 with cell size ~250m: 1.4 km radius captures the ~80% influence
// envelope from a single station per the existing interpolation tuning.
export const CAMERA_INFLUENCE_RADIUS_M = 1400;

// Vibe score: synthesized per-camera fog reading at a given moment. Wraps the
// camera's tier-derived amplitude around the SF marine-layer diurnal cycle
// (peak ~5am, trough ~5pm) with a small per-camera phase jitter so the map
// doesn't render every dot in unison.
export function cameraVibeScore(camera: CaltransCamera, timeMs: number): number {
  const amplitude = camera.vibeAmplitude ?? 0;
  const baseline = camera.vibeBaseline ?? 0;
  if (amplitude === 0 && baseline === 0) return 0;
  // Hour-of-day in UTC. PDT is UTC-7, so 5am PDT = 12:00 UTC peak fog window.
  const hour = ((timeMs / 3_600_000) % 24 + 24) % 24;
  // 0..1 envelope: 1 at hour 12 UTC (≈5am PDT), 0 at hour 0/24 UTC (≈5pm PDT).
  const envelope = 0.5 + 0.5 * Math.cos(((hour - 12) / 24) * Math.PI * 2);
  const idHash = [...camera.id].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  // Larger jitter + a faster wobble so dots visibly breathe even without
  // moving the scrubber.
  const jitter = Math.sin(timeMs / 3_600_000 * 0.9 + idHash * 0.17) * 0.18
    + Math.sin(timeMs / 60_000 * 0.07 + idHash * 0.41) * 0.10;
  return Math.max(0, Math.min(1, baseline + envelope * amplitude + jitter * amplitude));
}
